import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type Adjustment,
  type MovementRow,
  type Purchase,
  type Receipt,
  type StockOnHandRow,
  type SupplierReturn,
  type Transfer,
} from '../api/types'
import { formatMoney, formatMoment, formatQty } from '../ui/format'
import { Banner, Empty, Panel, Spinner } from '@shop/ui'
import { movementSourceNames, movementTypeNames } from '../ui/purchasing'

/**
 * لوحةُ المرحلة الثانية — <b>ما يحتاج متابعةً اليوم</b>.
 *
 * ولا «تحت الحدّ الأدنى» فيها: لا سياسةَ حدٍّ أدنى بعد (<c>reorder_point</c>)،
 * وحدٌّ مخمَّنٌ إنذارٌ يُتجاهَل — ثمّ لا يُصدَّق يومَ يصدق.
 */
export default function PhaseTwoBoard() {
  const { can } = useAuth()
  const today = new Date().toISOString().slice(0, 10)

  const purchases = useQuery({
    queryKey: ['purchases', 'CONFIRMED'],
    queryFn: () => api<Purchase[]>('/api/v1/purchases?status=CONFIRMED'),
    enabled: can(Perm.purchaseView),
  })

  const receipts = useQuery({
    queryKey: ['receipts', 'POSTED'],
    queryFn: () => api<Receipt[]>('/api/v1/receipts?status=POSTED'),
    enabled: can(Perm.receiptView),
  })

  const returns = useQuery({
    queryKey: ['returns', 'POSTED'],
    queryFn: () => api<SupplierReturn[]>('/api/v1/supplier-returns?status=POSTED'),
    enabled: can(Perm.returnView),
  })

  const adjustments = useQuery({
    queryKey: ['adjustments', 'POSTED'],
    queryFn: () => api<Adjustment[]>('/api/v1/adjustments?status=POSTED'),
    enabled: can(Perm.adjustmentView),
  })

  const transfers = useQuery({
    queryKey: ['transfers', 'SHIPPED'],
    queryFn: () => api<Transfer[]>('/api/v1/transfers?status=SHIPPED'),
    enabled: can(Perm.transferView),
  })

  const stock = useQuery({
    queryKey: ['stock', null, '', false],
    queryFn: () => api<StockOnHandRow[]>('/api/v1/stock/on-hand'),
    enabled: can(Perm.movementView),
  })

  const movements = useQuery({
    queryKey: ['movements', 'recent'],
    queryFn: () => api<MovementRow[]>('/api/v1/stock/movements'),
    enabled: can(Perm.movementView),
  })

  const pending = (purchases.data ?? []).filter((p) => p.receiptState === 'NOT_RECEIVED')
  const partial = (purchases.data ?? []).filter((p) => p.receiptState === 'PARTIALLY_RECEIVED')

  const receiptsToday = (receipts.data ?? []).filter((r) => r.receiptDate === today)
  const returnsToday = (returns.data ?? []).filter((r) => r.returnDate === today)
  const adjustmentsToday = (adjustments.data ?? []).filter((a) => a.adjustmentDate === today)

  const units = (stock.data ?? []).reduce((sum, row) => sum + row.qtyOnHand, 0)
  const value = (stock.data ?? []).reduce((sum, row) => sum + row.stockValue, 0)
  const uncosted = (stock.data ?? []).some((row) => row.uncostedMovements > 0)

  const maySeeCost = can(Perm.costView)

  return (
    <div className="space-y-4">
      <Panel title="ما يحتاج متابعة">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {can(Perm.purchaseView) && (
            <>
              <Tile
                to="/purchases?status=CONFIRMED"
                label="أوامرُ شراءٍ لم تُستلَم"
                value={pending.length}
                loading={purchases.isPending}
              />

              <Tile
                to="/purchases?status=CONFIRMED"
                label="استُلمت جزئيًّا"
                value={partial.length}
                loading={purchases.isPending}
                tone={partial.length > 0 ? 'warn' : undefined}
              />
            </>
          )}

          {can(Perm.transferView) && (
            <Tile
              to="/transfers?status=SHIPPED"
              label="بضاعةٌ في الطريق"
              value={transfers.data?.length ?? 0}
              loading={transfers.isPending}
              tone={(transfers.data?.length ?? 0) > 0 ? 'warn' : undefined}
              hint={
                (transfers.data ?? []).reduce((sum, t) => sum + t.inTransitQty, 0) > 0
                  ? `${formatQty((transfers.data ?? []).reduce((sum, t) => sum + t.inTransitQty, 0))} قطعة`
                  : undefined
              }
            />
          )}
        </div>
      </Panel>

      <Panel title="حركةُ اليوم">
        <div className="grid gap-3 sm:grid-cols-3">
          {can(Perm.receiptView) && (
            <Tile to="/receipts" label="إيصالاتُ استلام" value={receiptsToday.length} loading={receipts.isPending} />
          )}

          {can(Perm.returnView) && (
            <Tile to="/supplier-returns" label="مرتجعاتٌ لمورّدين" value={returnsToday.length} loading={returns.isPending} />
          )}

          {can(Perm.adjustmentView) && (
            <Tile to="/adjustments" label="تسويات" value={adjustmentsToday.length} loading={adjustments.isPending} />
          )}
        </div>
      </Panel>

      {can(Perm.movementView) && (
        <Panel title="المخزون">
          <div className="grid gap-3 sm:grid-cols-2">
            <Tile to="/stock" label="إجماليُّ القطع" value={units} loading={stock.isPending} format="qty" />

            {/* ⚠ والقيمةُ لمن يملك رؤيةَ الكلفة وحدَه — **والخادمُ يحجبها** لا الشاشة */}
            {maySeeCost && (
              <Tile
                to="/stock"
                label="قيمةُ المخزون"
                value={value}
                loading={stock.isPending}
                format="money"
                hint={uncosted ? 'حدٌّ أدنى — بعضُ الحركات بلا كلفة' : undefined}
                tone={uncosted ? 'warn' : undefined}
              />
            )}
          </div>

          {uncosted && maySeeCost && (
            <div className="mt-3">
              <Banner tone="warn">
                بعضُ الحركات بلا كلفة، فقيمةُ المخزون <b>حدٌّ أدنى لا رقمٌ تامّ</b>.
              </Banner>
            </div>
          )}
        </Panel>
      )}

      {can(Perm.movementView) && (
        <Panel title="آخرُ الحركات">
          {movements.isPending && <Spinner />}
          {movements.data?.length === 0 && <Empty>لا حركاتٍ بعد.</Empty>}

          <ul className="space-y-2">
            {(movements.data ?? []).slice(0, 12).map((movement) => (
              <li key={movement.id} className="rounded-md border border-line px-3 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm">
                    {movement.productName}
                    <span className={`tabular mx-2 ${movement.direction === 1 ? 'text-ok' : 'text-warn'}`}>
                      {movement.direction === 1 ? '+' : '−'}{formatQty(movement.qtyBase)}
                    </span>
                  </span>

                  <span className="tabular text-xs text-ink-soft" dir="ltr">{movement.movementNo}</span>
                </div>

                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                  <span>{movementTypeNames[movement.movementType] ?? movement.movementType}</span>
                  <span>{movement.warehouseName}</span>

                  {movement.sourceNo && (
                    <span className="tabular" dir="ltr">
                      {movementSourceNames[movement.sourceType ?? ''] ?? movement.sourceType}
                      {' '}{movement.sourceNo}
                    </span>
                  )}

                  <span>{formatMoment(movement.occurredAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  )
}

/** بطاقةُ رقمٍ واحد — والرقمُ صفرٌ يُعرض ولا يُخفى: «لا شيء» خبرٌ أيضًا. */
function Tile({
  to,
  label,
  value,
  loading,
  tone,
  hint,
  format = 'count',
}: {
  to: string
  label: string
  value: number
  loading: boolean
  tone?: 'warn'
  hint?: string
  format?: 'count' | 'qty' | 'money'
}) {
  const shown = format === 'money' ? formatMoney(value)
    : format === 'qty' ? formatQty(value)
      : value.toString()

  return (
    <Link
      to={to}
      className="block rounded-md border border-line px-3 py-3 hover:bg-surface"
    >
      <div className={`tabular text-2xl font-semibold ${tone === 'warn' ? 'text-warn' : ''}`}>
        {loading ? '…' : shown}
      </div>

      <div className="mt-1 text-xs text-ink-soft">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-warn">{hint}</div>}
    </Link>
  )
}
